import { useState, useEffect } from "react";
import { ChevronLeft } from "lucide-react";
import { Button } from "../ui/button";
import { DetailsFields, ParticipantsFields } from "./ExpenseFormFields";
import type { Participant, CurrencyCode } from "../../types";

interface ExpenseFormProps {
  participants: Participant[];
  currency: CurrencyCode;
  onSubmit: (
    description: string,
    amount: number,
    paidById: string,
    participantIds: string[]
  ) => void;
  onCancel: () => void;
  initialValues?: {
    description: string;
    amount: number;
    paidById: string;
    participantIds: string[];
  };
  submitLabel?: string;
}

export function ExpenseForm({
  participants,
  currency,
  onSubmit,
  onCancel,
  initialValues,
  submitLabel = "Agregar gasto",
}: ExpenseFormProps) {
  const [step, setStep] = useState(1);
  const [description, setDescription] = useState(
    initialValues?.description || ""
  );
  const [amount, setAmount] = useState(initialValues?.amount || 0);
  const [paidById, setPaidById] = useState(initialValues?.paidById || "");
  const [participantIds, setParticipantIds] = useState<string[]>(
    initialValues?.participantIds || participants.map((p) => p.id)
  );

  // Set default payer to first participant if not set
  useEffect(() => {
    const firstParticipant = participants[0];
    if (!paidById && firstParticipant) {
      setPaidById(firstParticipant.id);
    }
  }, [paidById, participants]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      // Go to step 2
      if (isStep1Valid) {
        setStep(2);
      }
    } else {
      // Submit the form
      if (isStep2Valid) {
        onSubmit(description.trim(), amount, paidById, participantIds);
      }
    }
  };

  const isStep1Valid = description.trim() && amount > 0 && paidById;
  const isStep2Valid = participantIds.length > 0;

  const step1ValidationMessage = !description.trim()
    ? "Ingresa una descripción"
    : amount <= 0
    ? "Ingresa un monto válido"
    : !paidById
    ? "Selecciona quién pagó"
    : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {step === 1 ? (
        <>
          <DetailsFields
            description={description}
            amount={amount}
            paidById={paidById}
            currency={currency}
            participants={participants}
            onChangeDescription={setDescription}
            onChangeAmount={setAmount}
            onChangePaidBy={setPaidById}
          />

          {step1ValidationMessage && (
            <p className="text-sm text-muted-foreground">
              {step1ValidationMessage}
            </p>
          )}

          <div className="flex gap-2 pt-2 pb-[env(safe-area-inset-bottom)]">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={!isStep1Valid} className="flex-1">
              Siguiente
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 -mt-2 mb-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={() => setStep(1)}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Atrás
            </Button>
            <span className="text-sm text-muted-foreground">Paso 2 de 2</span>
          </div>

          <ParticipantsFields
            participants={participants}
            selectedIds={participantIds}
            onChange={setParticipantIds}
          />

          {!isStep2Valid && (
            <p className="text-sm text-muted-foreground">
              Selecciona al menos un participante
            </p>
          )}

          <div className="flex gap-2 pt-2 pb-[env(safe-area-inset-bottom)]">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(1)}
              className="flex-1"
            >
              Atrás
            </Button>
            <Button type="submit" disabled={!isStep2Valid} className="flex-1">
              {submitLabel}
            </Button>
          </div>
        </>
      )}
    </form>
  );
}
